import { ComponentType, FC, createContext, useContext } from 'react';

import {
  buildAppListSortOptions,
  buildConnectorFilterOptionsOptions,
  buildTaskListSortOptions,
  buildWorkflowListSortOptions,
} from '@/common/utils/select-options-builders';

import { DEFAULT_LOCALE, translationMessages } from './i18n';
import { IResource, IResourceProviderProps, IWithResources } from './interface';

const translationMessage = translationMessages[DEFAULT_LOCALE];
const defaultValue = {
  resource: { ...translationMessage },
  sortOptions: {
    workflowsListSortOptions: buildWorkflowListSortOptions(translationMessage),
    appSortOptions: buildAppListSortOptions(translationMessage),
    taskSortOptions: buildTaskListSortOptions(translationMessage),
  },
  filterOptions: {
    addStepConnectorFilterOptions: buildConnectorFilterOptionsOptions(translationMessage),
  },
};

export function getDefaultResource() {
  return { ...defaultValue };
}

const ResourceContext = createContext(defaultValue);

export const ResourceProvider = ({ children }: IResourceProviderProps) => {
  // TODO:PAP - handle changing language
  return <ResourceContext.Provider value={defaultValue}>{children}</ResourceContext.Provider>;
};

// Overload of the withResources HOC
export function withResources<P extends IWithResources>(
  Component: ComponentType<P>
): FC<Omit<P, keyof IWithResources>>;

// Overload of the withResources HOC
export function withResources<P extends Translations, Translations>(
  Component: ComponentType<P>,
  resourceMapper?: (resource: IWithResources) => Translations
): FC<Omit<P, keyof Translations>>;

// TODO: remove this HOC in favor of the useResource hook
export function withResources<P extends IWithResources | Translations, Translations>(
  Component: ComponentType<P>,
  resourceMapper?: (resource: IWithResources) => Translations
): FC<Omit<P, keyof IWithResources | keyof Translations>> {
  return (props) => {
    const resource: IWithResources = useContext(ResourceContext);
    const resourceProps = resourceMapper ? resourceMapper(resource) : resource;
    const allProps = { ...resourceProps, ...props } as P & JSX.IntrinsicAttributes;

    return <Component {...allProps} />;
  };
}

export function useResource(): IWithResources;

export function useResource<Translations>(
  resourceMapper?: (resource: IResource) => Translations
): Translations;

export function useResource<Translations>(
  resourceMapper?: (resource: IResource) => Translations
): IResource | Translations {
  const { resource }: IWithResources = useContext(ResourceContext);
  return resourceMapper ? resourceMapper(resource) : resource;
}
